import http from "@/services/http";
import { featureFlags } from "@/config/env";

// 用户基本信息接口
export interface UserProfile {
  id: string;
  name: string;
  account: string;
  email: string;
  mobile: string;
  avatar: string;
  status: number;
  is_beta: number;
  created_at: string;
  updated_at: string;
  last_login_at: string;
  last_login_ip: string;
  beta_data?: BetaData;
}

// 内测联盟数据接口
export interface BetaData {
  id: string;
  uid: string;
  name: string;
  company_name: string;
  industry_name: string;
  business: string;
  position: string;
  email: string;
  audit_status: number; // 审核状态 1 已审核 0 待审核
  created_at: string;
  updated_at: string;
}

// 获取用户信息参数
export type GetUserProfileParams = {
  user_id: string;
};

// 获取用户信息响应
export type GetUserProfileResponse = {
  code: number;
  message: string;
  data: UserProfile;
};

/**
 * 获取用户信息
 * @param params 用户ID参数
 * @returns 用户信息
 */
export async function getUserProfile(
  params: GetUserProfileParams
): Promise<GetUserProfileResponse> {
  console.log("[getUserProfile] 开始获取用户信息，参数:", params);

  try {
    const res = await http.get<GetUserProfileResponse>("v1/user/profile", {
      params: {
        user_id: params.user_id,
      },
    });

    console.log("[getUserProfile] API响应数据:", res.data);

    if (res.data.code === 200) {
      console.log("[getUserProfile] 获取用户信息成功，用户ID:", params.user_id);
    } else {
      console.warn(
        "[getUserProfile] 获取用户信息失败，错误码:",
        res.data.code,
        "错误信息:",
        res.data.message
      );
    }

    return res.data;
  } catch (error) {
    console.error("[getUserProfile] 获取用户信息异常:", error);
    throw error;
  }
}

// Mock数据（用于开发环境）
const mockUserProfile: UserProfile = {
  id: "01999a36-1ad5-7c47-8e25-7e4fba510037",
  name: "测试用户",
  account: "13800138000",
  email: "test@example.com",
  mobile: "13800138000",
  avatar: "",
  status: 1,
  is_beta: 1,
  created_at: "2025-09-30T18:44:20.593+08:00",
  updated_at: "2025-10-17T19:29:00.594+08:00",
  last_login_at: "2025-10-17T19:29:00.594+08:00",
  last_login_ip: "192.168.1.1",
  beta_data: {
    id: "01999a39-260d-773b-bb7f-d3ddc799ef54",
    uid: "0199dcc0-91af-76d6-a51e-08d81ca7da00",
    name: "张三",
    company_name: "上海智推时代",
    industry_name: "互联网",
    business: "",
    position: "产品经理",
    email: "zhangsan@zhitui.com",
    audit_status: 1,
    created_at: "2025-09-30T18:44:20.593+08:00",
    updated_at: "2025-10-17T19:29:00.594+08:00",
  },
};

// Mock实现
async function getUserProfileMock(
  params: GetUserProfileParams
): Promise<GetUserProfileResponse> {
  console.log("[mock] get-user-profile", params);

  // 模拟网络延迟
  await new Promise((resolve) => setTimeout(resolve, 500));

  // 验证用户ID
  if (!params.user_id) {
    return {
      code: 201,
      message: "获取用户失败",
      data: mockUserProfile,
    };
  }

  return {
    code: 200,
    message: "获取成功",
    data: mockUserProfile,
  };
}

// 根据环境选择使用Mock还是真实接口
const useMock = featureFlags.enableMock;

// 导出最终的函数
export const getUserProfileApi = useMock ? getUserProfileMock : getUserProfile;

/*

## 4. 获取用户信息

### 调用示例

```bash
curl -X GET "https://test-api.zhituishidai.com/api/v1/user/profile?user_id=01999a36-1ad5-7c47-8e25-7e4fba510037" \
-H "Content-Type: application/json" \
-H "Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
```

### 错误码说明

| 错误码 | 错误信息 | 说明 |
|--------|----------|------|
| 201 | 获取用户失败 |  |


### 用户字段说明

| 字段名 | 类型 | 描述 |
|--------|------|------|
| id | string | 用户唯一标识 |
| name | string | 用户姓名（可为空） |
| account | string | 用户账号（默认为手机号） |
| email | string | 邮箱地址 |
| mobile | string | 手机号码 |
| avatar | string | 头像URL |
| status | int | 账户状态：1-正常，0-禁用 |
| is_beta | int | 内测状态：1-已加入，0-未加入 |
| created_at | string | 注册时间 |
| updated_at | string | 更新时间 |
| last_login_at | string | 最后登录时间 |
| last_login_ip | string | 最后登录IP |
| last_login_ip | string | 最后登录IP |
---
// 如果有加入内测联盟 会有以下数据
 "beta_data":
 {
    "id": "01999a39-260d-773b-bb7f-d3ddc799ef54",
    "uid": "0199dcc0-91af-76d6-a51e-08d81ca7da00",
    "name": "张三",
    "company_name": "上海智推时代",
    "industry_name": "互联网",
    "business": "",
    "position": "产品经理",
    "email": "zhangsan@zhitui.com",
    "audit_status": 0, // 审核状态 1 已审核 0 待审核
    "created_at": "2025-09-30T18:44:20.593+08:00",
    "updated_at": "2025-10-17T19:29:00.594+08:00"
}

*/
